export interface LegalBlock {
  kind: 'paragraph' | 'heading' | 'subheading';
  text: string;
  strongFrom?: number;
  links?: readonly { label: string; href: string }[];
}

export interface LegalDocument {
  eyebrow: string;
  title: string;
  documentTitle: string;
  description: string;
  sectionClassName: string;
  blocks: readonly LegalBlock[];
}

export type LegalPageName = 'privacy-policy' | 'cookies-policy' | 'terms-of-use';

const contactDisclosure: readonly LegalBlock[] = [
  { kind: 'heading', text: 'Contact form — updated 14 October 2026' },
  {
    kind: 'paragraph',
    text: 'When you submit the contact form, your name, email address and message are sent through Web3Forms to yaron.fanger@ylabs.ch so that Ylabs LLC can respond to your enquiry. Web3Forms also processes technical information, including IP addresses, for service operation and spam prevention.',
  },
  {
    kind: 'paragraph',
    text: 'Web3Forms operates from India using infrastructure in several regions. Its privacy policy states that submissions may be retained for up to three years unless deleted earlier or a shorter retention period applies. Submitter IP and email addresses may be shared with its spam prevention providers. For questions about your enquiry or requests concerning your submitted data, contact yaron.fanger@ylabs.ch. Provider details:',
    links: [
      { label: 'Web3Forms privacy policy', href: 'https://web3forms.com/privacy' },
      { label: 'Web3Forms data processing terms', href: 'https://web3forms.com/dpa' },
    ],
  },
];

const privacyBlocks: readonly LegalBlock[] = [
  {
    kind: 'paragraph',
    text: `The user agrees to the processing of personal data (hereinafter - "Consent") freely, of his own free will and in his interest. Consent can be given by the User or his representative in any form that allows to confirm the fact of its receipt, unless otherwise provided by law.Including, but not limited to, Consent may be given:
- When signing contracts for the provision of services and other documents between the User and the Operator;
- Through explicit confirmation of Consent on the Operator's websites when the User accepts offer agreements, user agreements, filling out registration forms, subscription forms, etc.

The Operator does not collect or process special categories of personal data, the processing of which, according to the current legislation of Ukraine, requires the written consent of the User.

By providing Consent, the User thereby agrees to:processing of his personal data by the Operator for purposes in accordance with the section "Purposes of processing personal data" of this Policy; instruction by the Operator to process personal data to third parties and transfer them in accordance with the "Confidentiality and Security" section of this Policy.

The user has the right to revoke his Consent at any time by sending a notification to the Operator in accordance with the section "User management of his personal data" of this Policy.`,
  },
  {
    kind: 'heading',
    text: `1. Types and sources of personal data`,
  },
  {
    kind: 'paragraph',
    text: `With his Consent, the User allows the Operator to process the following personal data:
- Surname, name, patronymic (including separately);
- E-mail address;
- Contact number;
- Date of Birth;
- Country, city, address (including separately);
- Position (occupation), name of the organization;
- The photo;
- Link to a personal site;
- Accounts in social networks and messaging programs;

Personal data directly included in written agreements between the Operator and the User (for example, passport data, payment details).

The Operator does not have access to and does not process the User's payment data (for example, information about payment cards, electronic wallets, etc.) provided by them to third-party payment systems, where the User is redirected to pay for the Operator's services. The procedure for handling such personal data is determined by the rules of the respective payment systems, and not by this Policy.

Personal data of Users can be obtained by the Operator:in the process of providing services and using the Operator's sites (for example, when Users fill out registration forms on the site, subscribe to news or free materials, when concluding an agreement and making a purchase, etc.); when Users contact the Operator with a request by phone, e-mail or through the customer support service; from publicly available sources (for example, open profiles in social networks, etc.).`,
  },
  {
    kind: 'heading',
    text: `2. Purposes of processing personal data`,
  },
  {
    kind: 'paragraph',
    text: `The processing of personal data means the recording, systematization, accumulation, storage, clarification (update, change), extraction, use, transfer (distribution, provision, access), depersonalization, blocking, deletion, destruction of personal data that do not fall under special categories for processing which, according to the current legislation of Ukraine, requires the written consent of the User.

The processing of personal data is carried out by the Operator in order to:
- sale of goods and provision of services in accordance with contracts and agreements with Users, including invoicing, delivery of orders and other similar operations;
- receiving and processing feedback from Users, including conducting surveys, voting and other similar events;
- sending information and advertising materials of the Operator to the email addresses of the Users provided by them for these purposes;
- promoting goods and services through direct contacts with the User using communication means;
- analysis of the effectiveness of advertising campaigns;
- research and analysis of user preferences, product development and testing as well as for the purposes directly prescribed by the current legislation of Ukraine.`,
  },
  {
    kind: 'heading',
    text: `3. Privacy and security`,
  },
  {
    kind: 'paragraph',
    text: `Data can be transferred by the Operator to servers located on the territory of other states, in which the Operator's service providers are located and which provide adequate legislative protection of the rights of Users to process and transfer personal data. For example, if you use popular and reliable mailing services that ensure compliance with anti-spam laws, etc.

The operator has the right to entrust the processing of personal data of Users to third parties. For example, suppliers providing the Operator with hosting services, order execution, delivery of electronic mailings, etc. For each such person, a list of actions with personal data, processing purposes and the obligation to maintain confidentiality and ensure the security of personal data are established.

Personal data may also be provided to third parties in cases and in the manner directly prescribed by applicable law (for example, at the request of state authorities, etc.).

The operator and other persons who have gained access to the personal data of the Users are obliged not to disclose to third parties and not to distribute personal data without the consent of the Users, unless otherwise provided by law.

The operator takes the necessary legal, organizational and technical measures to protect personal data from unauthorized or accidental access to them, destruction, alteration, blocking, copying, provision, dissemination of personal data, as well as from other illegal actions in relation to personal data.

The user must also take certain reasonable measures to protect his data, as well as the systems, networks and services that he uses. If the User has reason to believe that the processing of his personal data by the Operator is no longer secure, he can inform the Operator about it in accordance with the section "Management of his personal data" of this Policy.`,
  },
  {
    kind: 'heading',
    text: `4. Personal data processing time`,
  },
  {
    kind: 'paragraph',
    text: `The Operator processes the personal data of the Users for the time necessary to fulfill the purposes described in the section "Purposes of processing personal data" of this Policy.

The operator destroys or depersonalises personal data upon achievement of the processing goals or if it is no longer necessary to achieve them.

Regardless of the purposes of the processing, the User at any time has the right to withdraw his consent to the processing of personal data. In this case, the User agrees that the Operator retains the right to process the User's personal data when required.`,
  },
  {
    kind: 'heading',
    text: `5. User management of his personal data`,
    strongFrom: 3,
  },
  {
    kind: 'paragraph',
    text: `The user has the right:
- to receive information from the Operator regarding the processing of his personal data, if such a right is not limited in accordance with applicable law;
- require the Operator to clarify his personal data, block or destroy them if the personal data is incomplete, outdated, inaccurate, illegally obtained or not necessary for the stated purpose of processing;
- take measures provided by law to protect their rights.

The user has the right at any time to revoke his Consent to the processing of personal data in whole or in part by sending the appropriate request to the Operator. Such a request must be sent to the e-mail address or by letter to the address of the Operator specified in this section.

Withdrawal of consent to the processing of personal data for the purpose of electronic mailing of information and advertising materials can also be provided by the User by canceling the subscription through the "Unsubscribe" link located at the bottom of the corresponding mailings.

User requests on the processing of personal data are accepted by the Operator.`,
  },
  {
    kind: 'heading',
    text: `6. Other data`,
    strongFrom: 3,
  },
  {
    kind: 'paragraph',
    text: `The Operator also uses cookies and other similar technologies, on the basis of which information is collected to help evaluate the effectiveness of the Operator's services and sites and improve them. For details on the use and management of cookies, see our Cookie Notice.`,
  },
  {
    kind: 'heading',
    text: `7. Publishing and updating the policy`,
    strongFrom: 3,
  },
  {
    kind: 'paragraph',
    text: `This Policy is publicly available and published through:

- Posting links to the Policy on the Operator's websites, including links to the Policy in contracts, agreements and other documents with Users, when necessary and acceptable. At its sole discretion, the Operator may periodically update this Policy.
- Changes to the Policy come into force from the moment the new version is published on the website. The fact that the User uses our services after making the appropriate changes means his consent and acceptance of the new version of the Policy.

The date of the last update and previous revisions are indicated at the beginning of this Policy.The Operator recommends that Users periodically re-read this Policy to familiarize themselves with its current version.`,
  },
];

const cookieBlocks: readonly LegalBlock[] = [
  {
    kind: 'paragraph',
    text: `Our site, like most others, uses cookies and other similar technologies (web beacons, pixel tags, etc.) to provide services that best meet your needs, as well as collect statistical information to analyze and improve our services and sites.This Cookie Notice (“Notice”) is part of the Privacy Policy.

By using this site, you agree to the use of cookies and other similar technologies in accordance with this Notice.If you do not agree that we use this type of files, you must set your browser settings accordingly or not use our website (for more details, see the section "How to manage cookies").`,
  },
  {
    kind: 'heading',
    text: `What is cookie and other similar technologies`,
  },
  {
    kind: 'paragraph',
    text: `A cookie is a small text file that is stored on your computer, smartphone or other device that you use to visit the site. Some of the pages visited may also collect information using pixel tags and web beacons, which are electronic images called one-pixel (1 × 1) or empty GIF images. Cookies may be placed on your device by us ("own" cookies) or by other operators ("third party" cookies).

We use two types of cookies on the site: "session cookies" and "persistent cookies". Session cookies are temporary files that remain on your device until you leave the site. Persistent cookies remain on your device for a long time or until you manually delete them (how long a cookie stays on your device will depend on the length or "lifetime" of a particular file and your browser settings). Based on these files, information is collected to help evaluate the effectiveness of our services and sites and improve them. For example, to find out when you visit our site, how you use it, how you interact with email newsletters and other content, remember your preferences, provide a secure and secure service for online transactions, etc.

The information collected by such files and technologies is of a general nature and is not associated with the identification of individual users. However, when you purchase specific products and services from us, we may use the contact information you provide in the transaction in combination with information from cookies and similar technologies to analyze how you use our site and improve it. The processing of such contact data is carried out by us in accordance with the Privacy Policy.`,
  },
  {
    kind: 'heading',
    text: `Types of cookies that can be used on our site`,
  },
  {
    kind: 'subheading',
    text: `Necessary cookies.`,
    strongFrom: 0,
  },
  {
    kind: 'paragraph',
    text: `These cookies enable you to visit sites and use their features. Disabling them will complicate the functioning of sites and may make their functions and services unavailable.`,
  },
  {
    kind: 'subheading',
    text: `Performance, efficiency and analytics cookies.`,
    strongFrom: 0,
  },
  {
    kind: 'paragraph',
    text: `These cookies help us understand how users interact with our site, show problems in its operation (for example, error messages), help us measure the effectiveness of our advertising campaigns and optimize them for users.`,
  },
  {
    kind: 'subheading',
    text: `Functional cookies.`,
    strongFrom: 0,
  },
  {
    kind: 'paragraph',
    text: `They help to remember the individual preferences of users and to personalize the content of the site for them.`,
  },
  {
    kind: 'subheading',
    text: `Advertising cookies.`,
    strongFrom: 0,
  },
  {
    kind: 'paragraph',
    text: `They are used to deliver targeted advertisements and help us measure the effectiveness of advertising campaigns. We may share these files with the appropriate ad systems and platforms where our ads are placed.`,
  },
  {
    kind: 'subheading',
    text: `Email.`,
  },
  {
    kind: 'paragraph',
    text: `We may also use technology to track whether you have opened, read, or forwarded certain messages we send to your email. This is necessary to make our communication tools more useful to the user. If you do not wish us to be informed of this, you must unsubscribe via the “Unsubscribe” link at the bottom of the relevant email newsletter.`,
  },
  {
    kind: 'subheading',
    text: `Social media buttons.`,
    strongFrom: 0,
  },
  {
    kind: 'paragraph',
    text: `They are used to enable users to share a link to a page on social networks or make an electronic bookmark. These buttons are links to social networking websites owned by third parties, which, in turn, may record information about your activity on the Internet, including on our website. Please read the respective terms of use and privacy policies of those sites to understand how they use your data and how you can opt-out or delete your data.`,
  },
  {
    kind: 'subheading',
    text: `Third Party Web Services.`,
    strongFrom: 0,
  },
  {
    kind: 'paragraph',
    text: `We sometimes use third party web services on this site. For example, to display certain elements (images, videos, presentations, etc.), organize surveys, pay for products and services through payment systems, etc. As in the case of buttons for accessing social networks, we cannot prevent these sites or external domains from collecting information about how you use the site's content.`,
  },
  {
    kind: 'heading',
    text: `Changes to the terms of use of cookies
`,
  },
  {
    kind: 'paragraph',
    text: `In our sole discretion, we may change this Notice from time to time.Changes to the Notice come into effect from the moment the new version is published on the website. The fact that you are using our site after the relevant changes have been made constitutes your consent to accept the new version of the Notice. The date of last update and previous revisions are indicated at the beginning of this Notice.

We recommend that you periodically re-read this Notice to familiarize yourself with its current version.`,
  },
];

// The exported Terms of Use page repeats the Privacy Policy heading and body.
// Keep the published content intact until replacement legal copy is supplied.
export const legalDocuments: Record<LegalPageName, LegalDocument> = {
  'privacy-policy': {
    eyebrow: 'Terms of use of personal data',
    title: 'Privacy Policy',
    documentTitle: 'Privacy Policy | Arcnova 2026',
    description: 'Privacy policy information for the Arcnova 2026 website.',
    sectionClassName: 'section_privacy',
    blocks: [...contactDisclosure, ...privacyBlocks],
  },
  'cookies-policy': {
    eyebrow: 'Conditions for tracking the activity of site users',
    title: 'Notice about the use of cookies',
    documentTitle: 'Cookies Policy | Arcnova 2026',
    description:
      'Cookie policy information and cookie preference controls for the Arcnova 2026 website.',
    sectionClassName: 'section_privacy-2',
    blocks: cookieBlocks,
  },
  'terms-of-use': {
    eyebrow: 'Terms of use of personal data',
    title: 'Privacy Policy',
    documentTitle: 'Terms of Use | Arcnova 2026',
    description: 'Legal information on the Arcnova 2026 website.',
    sectionClassName: 'section_privacy',
    blocks: privacyBlocks,
  },
};
